import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Wallet, Loader2, IndianRupee, MoreVertical, Eye, FileSpreadsheet, RefreshCw, CalendarDays, Clock, AlertTriangle, BadgeCheck, Undo2, Receipt, Download, Mail, Coins } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { exportToExcel } from '../lib/exportUtils';
import { EmployeeLink } from '../components/links/EntityLinks';
import { usePageSize } from '../context/PageSizeContext';
import { Pagination } from '../components/Pagination';
import SalaryArrearsModal from '../components/payroll/SalaryArrearsModal';
import OvertimeOverrideModal from '../components/payroll/OvertimeOverrideModal';

const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const PayrollPage = () => {
  const [payroll, setPayroll] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, total_payout: 0, paid: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [page, setPage] = useState(1);
  const { pageSize } = usePageSize();
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payslipBusy, setPayslipBusy] = useState(false);
  // Salary Arrears + OT override modals — opened from the per-row
  // dropdown. Refetches the payroll list after a save so the new
  // net_salary + arrears badge appear without a full reload.
  const [arrearsTarget, setArrearsTarget] = useState(null);
  const [otTarget, setOtTarget] = useState(null);

  const handleDownloadPayslip = async (p) => {
    if (!p) return;
    setPayslipBusy(true);
    try {
      // Use fetch directly so we can stream a binary attachment.
      const base = process.env.REACT_APP_BACKEND_URL;
      const tokenRaw = localStorage.getItem('access_token') || '';
      const r = await fetch(`${base}/api/payroll/${p.id}/payslip-pdf?ts=${Date.now()}`, {
        headers: { Authorization: `Bearer ${tokenRaw}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Download failed');
      }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payslip-${p.employee_name?.replace(/\s+/g, '-') || p.id}-${p.month}-${p.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally { setPayslipBusy(false); }
  };

  const handleEmailPayslip = async (p) => {
    if (!p) return;
    setPayslipBusy(true);
    try {
      const r = await api.post(`/payroll/${p.id}/email`);
      toast.success(r.data?.message || 'Payslip emailed to employee');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email send failed');
    } finally { setPayslipBusy(false); }
  };
  const [summary, setSummary] = useState(null);

  const monthLabel = MONTHS[parseInt(selectedMonth) - 1];
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  // Mark-as-Paid flow state
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidRows, setMarkPaidRows] = useState([]); // [] = single row, or array for bulk
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payForm, setPayForm] = useState({ bank_account_id: '', payment_mode: 'bank_transfer', payment_date: new Date().toISOString().slice(0, 10) });
  const [marking, setMarking] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Fetch bank accounts once
  useEffect(() => {
    api.get('/transactions/bank-accounts').then(r => {
      if (r.data?.success) setBankAccounts(r.data.data || []);
    }).catch(() => {});
  }, []);

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        month: monthLabel,
        year: selectedYear,
        page: String(page),
        limit: String(pageSize),
      });
      const response = await api.get(`/payroll?${params.toString()}`);
      if (response.data.success) {
        setPayroll(response.data.data);
        setTotal(response.data.total || 0);
        // Track the freshest `created_at` so the confirmation modal can
        // warn that payroll was already generated for this period.
        const rows = response.data.data || [];
        if (rows.length) {
          const newest = rows.reduce((acc, r) => {
            const t = r.created_at || r.updated_at;
            return (!acc || (t && t > acc)) ? t : acc;
          }, null);
          setGeneratedAt(newest);
        } else { setGeneratedAt(null); }
      }
    } catch {
      setPayroll([]);
      setTotal(0);
      setGeneratedAt(null);
    } finally { setLoading(false); }
  }, [monthLabel, selectedYear, page, pageSize]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get(`/payroll/stats/summary?month=${monthLabel}&year=${selectedYear}`);
      if (res.data.success) setStats(res.data.data);
    } catch {
      setStats({ total: 0, total_payout: 0, paid: 0, pending: 0 });
    }
  }, [monthLabel, selectedYear]);

  // Reset page when filter changes so we always land on page 1 of new month
  useEffect(() => { setPage(1); }, [selectedMonth, selectedYear]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleGenerate = async () => {
    setConfirmOpen(false);
    setGenerating(true);
    try {
      const res = await api.post('/payroll/generate', {
        month: parseInt(selectedMonth),
        year: parseInt(selectedYear),
      });
      if (res.data.success) {
        setSummary(res.data.summary);
        toast.success(`Payroll generated for ${res.data.summary.month} ${res.data.summary.year}`);
        fetchPayroll();
        fetchStats();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate payroll');
    } finally { setGenerating(false); }
  };

  const openMarkPaid = (rows) => {
    setMarkPaidRows(rows);
    // Auto-pick primary bank if available; otherwise leave blank to force a choice.
    const primary = bankAccounts.find(b => b.is_primary) || bankAccounts[0];
    setPayForm({
      bank_account_id: primary?.id || '',
      payment_mode: 'bank_transfer',
      payment_date: new Date().toISOString().slice(0, 10),
    });
    setMarkPaidOpen(true);
  };

  const handleMarkPaid = (item) => openMarkPaid([item]);

  const handleRegeneratePayslip = async (item) => {
    const isPaid = item.status === 'paid' || !!item.transaction_id;
    const oldNet = (item.net_salary || 0).toLocaleString('en-IN');
    const confirmText = isPaid
      ? `Regenerate the payslip for ${item.employee_name} (${item.month} ${item.year})?\n\n` +
        `This will recompute salary, attendance, LOP and PT from the latest data.\n` +
        `Current net salary: ₹${oldNet}\n\n` +
        `⚠ This row is already PAID. The payment status, transaction, and bank entry will NOT be touched. ` +
        `If the new net differs from the paid amount, the row will be flagged with a "payment mismatch" — you'll need to issue a manual adjustment.\n\n` +
        `Proceed?`
      : `Regenerate the payslip for ${item.employee_name} (${item.month} ${item.year})?\n\n` +
        `This will recompute salary, attendance, LOP and PT from the latest data.\n` +
        `Current net salary: ₹${oldNet}\n\n` +
        `The payment status will remain "pending".\n\nProceed?`;
    const ok = window.confirm(confirmText);
    if (!ok) return;
    const tid = toast.loading('Regenerating payslip…');
    try {
      const res = await api.post(`/payroll/${item.id}/regenerate`);
      if (res.data?.success) {
        const d = res.data;
        const delta = (d.delta || 0);
        const msg = d.net_changed
          ? `Payslip regenerated. Net salary changed: ₹${(d.old_net || 0).toLocaleString('en-IN')} → ₹${(d.new_net || 0).toLocaleString('en-IN')} (${delta >= 0 ? '+' : ''}₹${delta.toLocaleString('en-IN')}).`
          : 'Payslip regenerated. Net salary unchanged.';
        toast.success(msg, { id: tid, duration: 6000 });
        fetchPayroll();
        fetchStats();
      } else {
        toast.error(res.data?.detail || 'Regenerate failed', { id: tid });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regenerate failed', { id: tid });
    }
  };

  const handleReversePayment = async (item) => {
    const ok = window.confirm(
      `Reverse the salary payment for ${item.employee_name}?\n\n` +
      `This will:\n` +
      `  • Soft-delete the linked salary transaction\n` +
      `  • Credit ₹${(item.net_salary || 0).toLocaleString('en-IN')} back to the bank account\n` +
      `  • Flip this payroll row back to "pending"\n\n` +
      `The original transaction stays in the audit trail.`
    );
    if (!ok) return;
    try {
      const res = await api.post(`/payroll/${item.id}/reverse-payment`);
      if (res.data?.success) {
        toast.success(`Payment of ₹${(res.data.data.amount_refunded || 0).toLocaleString('en-IN')} reversed for ${item.employee_name}`);
        fetchPayroll();
        fetchStats();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reversal failed');
    }
  };

  const submitMarkPaid = async () => {
    if (!payForm.bank_account_id) {
      toast.error('Please select a bank account');
      return;
    }
    setMarking(true);
    try {
      if (markPaidRows.length === 1) {
        await api.post(`/payroll/${markPaidRows[0].id}/mark-paid`, payForm);
        toast.success(`Marked ${markPaidRows[0].employee_name} as paid`);
      } else {
        const res = await api.post('/payroll/bulk-mark-paid', {
          payroll_ids: markPaidRows.map(r => r.id),
          ...payForm,
        });
        const d = res.data?.data || {};
        toast.success(`Marked ${d.marked || 0} payslip${d.marked === 1 ? '' : 's'} as paid · ₹${(d.total_amount || 0).toLocaleString('en-IN')} debited${d.skipped ? ` (${d.skipped} skipped — already paid)` : ''}`);
      }
      setMarkPaidOpen(false);
      setSelectedIds(new Set());
      fetchPayroll();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    } finally { setMarking(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectablePayroll = payroll.filter(p => !p.transaction_id && p.status !== 'paid');
  const allSelected = selectablePayroll.length > 0 && selectablePayroll.every(p => selectedIds.has(p.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectablePayroll.map(p => p.id)));
  };
  const selectedRows = payroll.filter(p => selectedIds.has(p.id));

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/payroll?month=${monthLabel}&year=${selectedYear}&limit=10000`);
      const rows = res.data?.data || [];
      if (!rows.length) { toast.info('No payroll rows to export'); return; }
      exportToExcel(rows, [
        { key: 'employee_name', header: 'Employee' },
        { key: 'employee_code', header: 'ID' },
        { key: 'basic_salary', header: 'Basic' },
        { key: 'hra', header: 'HRA' },
        { key: 'allowances', header: 'Allowances' },
        { key: 'overtime_pay', header: 'Overtime' },
        { key: 'total_deductions', header: 'Deductions' },
        { key: 'net_salary', header: 'Net Salary' },
        { key: 'status', header: 'Status' },
      ], `Payroll_${monthLabel}_${selectedYear}`);
      toast.success(`Exported ${rows.length} rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const getStatusBadge = (status) => {
    const cfg = { paid: 'bg-emerald-500/10 text-emerald-500', pending: 'bg-amber-500/10 text-amber-500', processing: 'bg-blue-500/10 text-blue-500' };
    return <Badge className={cfg[status] || cfg.pending}>{status}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Payroll</h1>
          <p className="text-muted-foreground">Generate and manage monthly salary processing</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedRows.length > 0 && (
            <Button onClick={() => openMarkPaid(selectedRows)} className="bg-emerald-600 hover:bg-emerald-600/90" data-testid="bulk-mark-paid-btn">
              <IndianRupee className="h-4 w-4 mr-1" />Mark {selectedRows.length} as Paid
            </Button>
          )}
          <Link to="/payroll/professional-tax">
            <Button variant="outline" size="sm" data-testid="pt-report-link">
              <Receipt className="h-4 w-4 mr-1" />PT Report
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exporting} data-testid="export-payroll-excel">
            {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
            Excel
          </Button>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[130px]" data-testid="payroll-month-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[90px]" data-testid="payroll-year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: (new Date().getFullYear() - 2015) + 2 }, (_, i) => 2015 + i)
                .reverse()
                .map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setConfirmOpen(true)} disabled={generating} data-testid="generate-payroll-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Generating...' : 'Generate Payroll'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Wallet className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold" data-testid="payroll-emp-count">{stats.total}</p><p className="text-xs text-muted-foreground">Employees</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-emerald-500" /></div><div><p className="text-xl font-bold" data-testid="payroll-total">{formatCurrency(stats.total_payout)}</p><p className="text-xs text-muted-foreground">Total Payout</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Wallet className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold" data-testid="payroll-paid">{stats.paid}</p><p className="text-xs text-muted-foreground">Paid</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><Wallet className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold" data-testid="payroll-pending">{stats.pending}</p><p className="text-xs text-muted-foreground">Pending</p></div></div></CardContent></Card>
      </div>

      {summary && (
        <Card className="border-primary/20 bg-primary/5" data-testid="payroll-summary">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /><span className="text-muted-foreground">Working Days:</span><span className="font-semibold">{summary.total_working_days}</span></div>
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-amber-500" /><span className="text-muted-foreground">Holidays:</span><span className="font-semibold">{summary.holidays_count}</span></div>
              <div className="flex items-center gap-2"><IndianRupee className="h-4 w-4 text-emerald-500" /><span className="text-muted-foreground">Total Payout:</span><span className="font-semibold">{formatCurrency(summary.total_payout)}</span></div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        payroll.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Wallet className="h-12 w-12 mb-4 opacity-50" />
            <p>No payroll data for {monthLabel} {selectedYear}</p>
            <Button variant="outline" className="mt-4" onClick={() => setConfirmOpen(true)} data-testid="generate-payroll-empty">
              <RefreshCw className="h-4 w-4 mr-2" /> Generate Now
            </Button>
          </div>
        ) :
        <Table data-testid="payroll-table"><TableHeader><TableRow>
          <TableHead className="w-[40px]">
            <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} aria-label="Select all" data-testid="payroll-select-all" />
          </TableHead>
          <TableHead>Employee</TableHead>
          <TableHead>Basic</TableHead>
          <TableHead>HRA</TableHead>
          <TableHead>Days</TableHead>
          <TableHead>Overtime</TableHead>
          <TableHead>Deductions</TableHead>
          <TableHead>Net Salary</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-[60px]"></TableHead>
        </TableRow></TableHeader>
        <TableBody>{payroll.map(p => (
          <TableRow key={p.id} data-testid={`payroll-row-${p.id}`}>
            <TableCell>
              {p.transaction_id || p.status === 'paid' ? (
                <span className="text-muted-foreground text-xs">—</span>
              ) : (
                <Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`payroll-select-${p.id}`} />
              )}
            </TableCell>
            <TableCell><div><p className="font-medium"><EmployeeLink id={p.employee_id} name={p.employee_name} /></p><p className="text-xs text-muted-foreground">{p.employee_code}</p></div></TableCell>
            <TableCell className="text-sm">{formatCurrency(p.basic_salary)}</TableCell>
            <TableCell className="text-sm">{formatCurrency(p.hra)}</TableCell>
            <TableCell>
              <div className="text-sm">
                <span className="font-medium">{p.days_present}</span>
                <span className="text-muted-foreground">/{p.total_working_days}</span>
                {p.days_absent > 0 && <p className="text-xs text-destructive">{p.days_absent} absent</p>}
              </div>
            </TableCell>
            <TableCell>
              {p.overtime_days > 0 || (Number(p.overtime_pay) || 0) > 0 ? (
                <div className="text-sm">
                  {p.overtime_days > 0 && (
                    <span className="text-amber-500 font-medium">{p.overtime_days}d</span>
                  )}
                  <p className="text-xs text-muted-foreground">+{formatCurrency(p.overtime_pay)}</p>
                  {p.overtime_pay_override !== null && p.overtime_pay_override !== undefined && (
                    <p className="text-[10px] text-amber-600 font-medium" data-testid={`ot-override-badge-${p.id}`}>
                      Manual override
                    </p>
                  )}
                </div>
              ) : <span className="text-muted-foreground text-sm">-</span>}
            </TableCell>
            <TableCell>
              <div className="text-sm text-destructive">
                -{formatCurrency(p.total_deductions)}
                {p.absence_deduction > 0 && <p className="text-xs">({formatCurrency(p.absence_deduction)} absence)</p>}
              </div>
            </TableCell>
            <TableCell className="font-semibold">
              {formatCurrency(p.net_salary)}
              {(Number(p.arrears_amount) || 0) > 0 && (
                <p className="text-[10px] text-amber-600 font-medium mt-0.5" data-testid={`arrears-badge-${p.id}`}>
                  + Arrears {formatCurrency(p.arrears_amount)}
                </p>
              )}
            </TableCell>
            <TableCell>{getStatusBadge(p.status)}</TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`payroll-actions-${p.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { setSelectedPayslip(p); setIsViewOpen(true); }}><Eye className="h-4 w-4 mr-2" />View Payslip</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleRegeneratePayslip(p)} data-testid={`regenerate-payslip-${p.id}`}>
                  <RefreshCw className="h-4 w-4 mr-2" />Regenerate Payslip
                </DropdownMenuItem>
                {p.status !== 'paid' && (
                  <DropdownMenuItem
                    onClick={() => setArrearsTarget(p)}
                    data-testid={`arrears-open-${p.id}`}
                  >
                    <Coins className="h-4 w-4 mr-2 text-amber-500" />
                    {(Number(p.arrears_amount) || 0) > 0 ? 'Edit Salary Arrears' : 'Add Salary Arrears'}
                  </DropdownMenuItem>
                )}
                {p.status !== 'paid' && (
                  <DropdownMenuItem
                    onClick={() => setOtTarget(p)}
                    data-testid={`ot-override-open-${p.id}`}
                  >
                    <Clock className="h-4 w-4 mr-2 text-amber-500" />
                    {p.overtime_pay_override !== null && p.overtime_pay_override !== undefined
                      ? 'Edit Overtime Override'
                      : 'Override Overtime Pay'}
                  </DropdownMenuItem>
                )}
                {p.status !== 'paid' && !p.transaction_id && (
                  <DropdownMenuItem onClick={() => handleMarkPaid(p)} data-testid={`mark-paid-${p.id}`}>
                    <IndianRupee className="h-4 w-4 mr-2" />Mark as Paid
                  </DropdownMenuItem>
                )}
                {(p.status === 'paid' || p.transaction_id) && (
                  <DropdownMenuItem onClick={() => handleReversePayment(p)} data-testid={`reverse-payment-${p.id}`} className="text-amber-600 focus:text-amber-600">
                    <Undo2 className="h-4 w-4 mr-2" />Reverse Payment
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>))}</TableBody></Table>}
      </CardContent></Card>

      <Pagination
        page={page}
        total={total}
        pageSize={pageSize}
        onPageChange={setPage}
        testIdPrefix="payroll-pagination"
      />

      {/* Mark-as-Paid Dialog */}
      <Dialog open={markPaidOpen} onOpenChange={setMarkPaidOpen}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-emerald-500" />
              Mark {markPaidRows.length === 1 ? markPaidRows[0]?.employee_name : `${markPaidRows.length} payslips`} as Paid
            </DialogTitle>
            <DialogDescription className="pt-2">
              This records the salary payout in the Transactions ledger and updates the bank balance. {markPaidRows.length > 1 && 'One debit transaction will be created per employee.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">Total payout</span>
              <span className="font-semibold text-lg">{formatCurrency(markPaidRows.reduce((s, r) => s + (r.net_salary || 0), 0))}</span>
            </div>
            <div className="space-y-1.5">
              <Label>Bank Account *</Label>
              <Select value={payForm.bank_account_id} onValueChange={v => setPayForm(f => ({ ...f, bank_account_id: v }))}>
                <SelectTrigger data-testid="mark-paid-bank"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                <SelectContent>
                  {bankAccounts.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name || b.bank_name} {b.is_primary && '· Primary'}
                      <span className="text-xs text-muted-foreground ml-2">{formatCurrency(b.current_balance || 0)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Payment Mode</Label>
                <Select value={payForm.payment_mode} onValueChange={v => setPayForm(f => ({ ...f, payment_mode: v }))}>
                  <SelectTrigger data-testid="mark-paid-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Payment Date</Label>
                <input
                  type="date"
                  value={payForm.payment_date}
                  onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="mark-paid-date"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidOpen(false)}>Cancel</Button>
            <Button onClick={submitMarkPaid} disabled={marking || !payForm.bank_account_id} className="bg-emerald-600 hover:bg-emerald-600/90" data-testid="confirm-mark-paid">
              {marking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Payroll confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="generate-payroll-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Generate Payroll for {monthLabel} {selectedYear}?
            </DialogTitle>
            <DialogDescription className="pt-2">
              Make sure <strong>attendance has been finalised</strong> for every active employee for the selected month before generating payroll. Net salary, absence deductions, and overtime are all derived from the attendance ledger — any missing or incorrect day will flow straight into the payslip.
            </DialogDescription>
          </DialogHeader>
          {generatedAt && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm" data-testid="payroll-already-generated-warning">
              <div className="flex items-center gap-2 font-semibold text-amber-500"><AlertTriangle className="h-4 w-4" />Payroll for this period already exists</div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Generated on {new Date(generatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}. Proceeding will <strong>overwrite</strong> the existing rows with freshly-computed values based on the current attendance data.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating} data-testid="confirm-generate-payroll">
              {generating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payslip Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-md" data-testid="payslip-dialog">
          <DialogHeader><DialogTitle>Payslip Details</DialogTitle></DialogHeader>
          {selectedPayslip && <div className="space-y-4">
            <div className="flex justify-between items-start">
              <div><p className="font-bold text-lg">{selectedPayslip.employee_name}</p><p className="text-sm text-muted-foreground">{selectedPayslip.employee_code} | {selectedPayslip.month} {selectedPayslip.year}</p></div>
              {getStatusBadge(selectedPayslip.status)}
            </div>
            {selectedPayslip.attendance_exempt && (
              <div className="rounded-md border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-xs text-violet-300" data-testid="payslip-exempt-chip">
                Attendance-exempt — full month treated as worked. Absence deduction skipped.
              </div>
            )}
            <div className="space-y-3 pt-2 border-t border-border">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">Earnings</h4>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Basic Salary</span><span>{formatCurrency(selectedPayslip.basic_salary)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">HRA (40%)</span><span>{formatCurrency(selectedPayslip.hra)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Allowances</span><span>{formatCurrency(selectedPayslip.allowances)}</span></div>
              {selectedPayslip.overtime_pay > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-amber-500">
                    Overtime
                    {selectedPayslip.overtime_pay_override !== null && selectedPayslip.overtime_pay_override !== undefined
                      ? ' (manual override)'
                      : ` (${selectedPayslip.overtime_days}d × ${formatCurrency(Math.round((selectedPayslip.daily_rate || 0) * (selectedPayslip.overtime_rate || 0)))}/day)`}
                  </span>
                  <span className="text-amber-500">+{formatCurrency(selectedPayslip.overtime_pay)}</span>
                </div>
              )}
              {(Number(selectedPayslip.arrears_amount) || 0) > 0 && (
                <>
                  {(selectedPayslip.arrears_breakdown || []).map((b, idx) => (
                    <div key={idx} className="flex justify-between text-sm" data-testid={`payslip-arrears-line-${idx}`}>
                      <span className="text-amber-500">
                        Arrears — {b.source_month} {b.source_year}
                        {b.note ? <span className="text-xs text-muted-foreground"> ({b.note})</span> : null}
                      </span>
                      <span className="text-amber-500">+{formatCurrency(b.amount)}</span>
                    </div>
                  ))}
                  {(!selectedPayslip.arrears_breakdown || selectedPayslip.arrears_breakdown.length === 0) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-amber-500">Salary Arrears</span>
                      <span className="text-amber-500">+{formatCurrency(selectedPayslip.arrears_amount)}</span>
                    </div>
                  )}
                </>
              )}

              <h4 className="text-xs font-semibold text-muted-foreground uppercase pt-2 border-t border-border">Attendance</h4>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Working Days</span><span>{selectedPayslip.total_working_days}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Days Present</span><span>{selectedPayslip.days_present}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Days Absent</span><span className={selectedPayslip.days_absent > 0 ? 'text-destructive' : ''}>{selectedPayslip.days_absent}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Daily Rate</span><span>{formatCurrency(selectedPayslip.daily_rate)}</span></div>

              <h4 className="text-xs font-semibold text-muted-foreground uppercase pt-2 border-t border-border">Deductions</h4>
              {selectedPayslip.statutory_deductions > 0 && (
                <div className="flex justify-between text-sm text-destructive"><span>Statutory (PF 12%)</span><span>-{formatCurrency(selectedPayslip.statutory_deductions)}</span></div>
              )}
              {selectedPayslip.professional_tax > 0 && (
                <div className="flex justify-between text-sm text-destructive">
                  <span>Profession Tax {selectedPayslip.professional_tax_yearly ? `(Yearly: ${formatCurrency(selectedPayslip.professional_tax_yearly)})` : ''}</span>
                  <span>-{formatCurrency(selectedPayslip.professional_tax)}</span>
                </div>
              )}
              {selectedPayslip.absence_deduction > 0 && (
                <div className="flex justify-between text-sm text-destructive"><span>Absence ({selectedPayslip.days_absent} days)</span><span>-{formatCurrency(selectedPayslip.absence_deduction)}</span></div>
              )}

              <div className="flex justify-between text-base font-bold border-t border-border pt-2">
                <span>Net Salary</span>
                <span className="text-primary">{formatCurrency(selectedPayslip.net_salary)}</span>
              </div>
            </div>
          </div>}
          {selectedPayslip && (
            <DialogFooter className="border-t border-border pt-4">
              <Button
                variant="outline"
                onClick={() => handleEmailPayslip(selectedPayslip)}
                disabled={payslipBusy}
                data-testid="payslip-email-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email to Employee
              </Button>
              <Button
                onClick={() => handleDownloadPayslip(selectedPayslip)}
                disabled={payslipBusy}
                data-testid="payslip-download-btn"
              >
                {payslipBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Download PDF
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Salary Arrears modal — opened from per-row dropdown */}
      <SalaryArrearsModal
        open={!!arrearsTarget}
        payroll={arrearsTarget}
        onClose={() => setArrearsTarget(null)}
        onSaved={() => { fetchPayroll(); fetchStats(); }}
      />
      {/* Overtime Override modal */}
      <OvertimeOverrideModal
        open={!!otTarget}
        payroll={otTarget}
        onClose={() => setOtTarget(null)}
        onSaved={() => { fetchPayroll(); fetchStats(); }}
      />
    </div>
  );
};
